import React, { useState, useEffect, useRef, useCallback } from 'react';

const AdminInfo = () => {
    const [lastUpdated, setLastUpdated] = useState('');
    const [activeMetric, setActiveMetric] = useState(null);
    const [systemStatus, setSystemStatus] = useState('operational');
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        // Simulate fetching last updated time or set it dynamically
        const now = new Date();
        setLastUpdated(now.toLocaleString());
        
        // Simulate real-time updates
        const interval = setInterval(() => {
            const now = new Date();
            setLastUpdated(now.toLocaleString());
        }, 30000); // Update every 30 seconds

        return () => clearInterval(interval);
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        const now = new Date();
        setLastUpdated(now.toLocaleString());
        setRefreshing(false);
    };

    const systemHealthData = [
        { label: 'Status', value: 'Operational', icon: 'fas fa-check-circle', color: 'green', bg: 'green-500', status: 'healthy' },
        { label: 'Uptime', value: '100.00%', subtext: '(Last 30 days)', icon: 'fas fa-clock', color: 'blue', bg: 'blue-500', status: 'excellent' },
        // { label: 'Response Time', value: '127ms', subtext: 'Average', icon: 'fas fa-tachometer-alt', color: 'emerald', bg: 'emerald-500', status: 'fast' },
        // { label: 'CPU Usage', value: '34%', subtext: 'Current', icon: 'fas fa-microchip', color: 'yellow', bg: 'yellow-500', status: 'normal' }
    ];

    const keyMetrics = [
        { label: 'Total Users', value: '5,421', change: '+12%', icon: 'fas fa-users', color: 'blue', bg: 'blue-500', trend: 'up' },
        { label: 'Active Servers', value: '128', change: '+3', icon: 'fas fa-server', color: 'purple', bg: 'purple-500', trend: 'up' },
        { label: 'Storage Used', value: '2.4TB', change: '68%', icon: 'fas fa-hdd', color: 'orange', bg: 'orange-500', trend: 'neutral' },
        { label: 'API Calls', value: '1.2M', change: '+8.5%', icon: 'fas fa-exchange-alt', color: 'cyan', bg: 'cyan-500', trend: 'up' }
    ];

    const quickActions = [
        { label: 'System Logs', icon: 'fas fa-file-alt', color: 'indigo', bg: 'indigo-500', description: 'View detailed system logs' },
        { label: 'Backup Status', icon: 'fas fa-database', color: 'emerald', bg: 'emerald-500', description: 'Check backup operations' },
        { label: 'Security Audit', icon: 'fas fa-shield-alt', color: 'red', bg: 'red-500', description: 'Review security reports' },
        { label: 'Performance', icon: 'fas fa-chart-line', color: 'yellow', bg: 'yellow-500', description: 'Monitor system performance' }
    ];

    return (
        <div className="relative h-full p-1 overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-cyan-900/20 rounded-3xl">
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl animate-float"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full blur-2xl animate-bounce-slow"></div>
            </div>

            <div className="relative z-10 glass-card rounded-3xl p-8 border border-white/10 backdrop-blur-xl shadow-2xl h-full overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between mb-10">
                    <div className="flex items-center">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-lg opacity-60 group-hover:opacity-80 transition-opacity animate-pulse"></div>
                            <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/20">
                                <i className="fas fa-info-circle text-2xl text-white animate-float"></i>
                            </div>
                        </div>
                        <div className="ml-6">
                            <h3 className="text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent animate-gradient">
                                System Overview
                            </h3>
                            <p className="text-gray-400 mt-2 animate-fade-in">Real-time system monitoring & analytics</p>
                        </div>
                    </div>
                    
                    <button 
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="relative group overflow-hidden bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative flex items-center space-x-2">
                            <i className={`fas fa-sync-alt ${refreshing ? 'animate-spin' : ''}`}></i>
                            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                        </div>
                    </button>
                </div>

                {/* System Health Dashboard */}
                <div className="glass-card rounded-2xl p-8 mb-8 border border-white/10 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full blur-2xl"></div>
                    
                    <div className="flex items-center mb-8 relative z-10">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-green-500/30 rounded-xl blur-md group-hover:blur-lg transition-all animate-pulse"></div>
                            <div className="relative w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-green-400/30">
                                <i className="fas fa-heartbeat text-xl text-green-400 animate-bounce-slow"></i>
                            </div>
                        </div>
                        <h4 className="text-2xl font-bold text-white ml-4 animate-slide-in">System Health</h4>
                        <div className="ml-auto">
                            <div className="flex items-center space-x-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-2">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                <span className="text-green-400 text-sm font-semibold">All Systems Operational</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {systemHealthData.map((item, index) => (
                            <div
                                key={index}
                                className="relative group cursor-pointer"
                                style={{ animationDelay: `${index * 100}ms` }}
                                onMouseEnter={() => setActiveMetric(`health-${index}`)}
                                onMouseLeave={() => setActiveMetric(null)}
                            >
                                <div className={`absolute inset-0 bg-gradient-to-br from-${item.bg}/20 to-${item.bg}/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500`}></div>
                                <div className={`relative glass-card rounded-xl p-6 border border-white/10 group-hover:border-${item.color}-400/30 transition-all duration-500 transform group-hover:scale-105 group-hover:-translate-y-2 animate-slide-up`}>
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`w-12 h-12 bg-${item.color}-500/20 rounded-xl flex items-center justify-center group-hover:bg-${item.color}-500/30 transition-colors`}>
                                            <i className={`${item.icon} text-xl text-${item.color}-400 group-hover:animate-pulse`}></i>
                                        </div>
                                        {activeMetric === `health-${index}` && (
                                            <div className={`w-3 h-3 bg-${item.color}-400 rounded-full animate-ping`}></div>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-400 mb-2">{item.label}</p>
                                        <p className="text-2xl font-bold text-white mb-1 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 group-hover:bg-clip-text transition-all">
                                            {item.value}
                                        </p>
                                        <div className="h-5 flex items-center">
                                            {item.subtext && (
                                                <p className={`text-${item.color}-400 text-sm font-medium`}>{item.subtext}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`absolute bottom-2 right-2 w-8 h-8 bg-${item.color}-500/10 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 animate-fade-in`}>
                                        <i className="fas fa-chevron-right text-xs text-white"></i>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Key Metrics with Charts */}
                {/* <div className="glass-card rounded-2xl p-8 mb-8 border border-white/10 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
                    
                    <div className="flex items-center mb-8 relative z-10">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-purple-500/30 rounded-xl blur-md group-hover:blur-lg transition-all animate-pulse"></div>
                            <div className="relative w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-purple-400/30">
                                <i className="fas fa-chart-bar text-xl text-purple-400 animate-bounce-slow"></i>
                            </div>
                        </div>
                        <h4 className="text-2xl font-bold text-white ml-4 animate-slide-in">Key Metrics</h4>
                        <div className="ml-auto flex items-center space-x-3">
                            <div className="flex items-center space-x-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-3 py-1">
                                <i className="fas fa-trend-up text-blue-400 text-xs animate-bounce"></i>
                                <span className="text-blue-400 text-sm font-semibold">Trending Up</span>
                            </div>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {keyMetrics.map((metric, index) => (
                            <div
                                key={index}
                                className="relative group cursor-pointer"
                                style={{ animationDelay: `${index * 150}ms` }}
                                onMouseEnter={() => setActiveMetric(`metric-${index}`)}
                                onMouseLeave={() => setActiveMetric(null)}
                            >
                                <div className={`absolute inset-0 bg-gradient-to-br from-${metric.bg}/20 to-${metric.bg}/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500`}></div>
                                <div className={`relative glass-card rounded-xl p-6 border border-white/10 group-hover:border-${metric.color}-400/30 transition-all duration-500 transform group-hover:scale-110 group-hover:-translate-y-3 animate-slide-up shadow-lg group-hover:shadow-2xl group-hover:shadow-${metric.color}-500/20`}>
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`w-12 h-12 bg-${metric.color}-500/20 rounded-xl flex items-center justify-center group-hover:bg-${metric.color}-500/30 transition-all duration-300 group-hover:scale-110`}>
                                            <i className={`${metric.icon} text-xl text-${metric.color}-400 group-hover:animate-pulse`}></i>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            {metric.trend === 'up' && (
                                                <div className="flex items-center space-x-1 bg-green-500/20 rounded-full px-2 py-1">
                                                    <i className="fas fa-arrow-up text-green-400 text-xs animate-bounce"></i>
                                                    <span className="text-green-400 text-xs font-semibold">{metric.change}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-400 mb-2">{metric.label}</p>
                                        <p className="text-3xl font-bold text-white mb-1 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-white group-hover:via-gray-200 group-hover:to-gray-300 group-hover:bg-clip-text transition-all">
                                            {metric.value}
                                        </p>
                                        {metric.subtext && (
                                            <p className={`text-${metric.color}-400 text-sm font-medium`}>{metric.subtext}</p>
                                        )}
                                    </div>
                                    
                                    {activeMetric === `metric-${index}` && (
                                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/10 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                            <div className="text-center">
                                                <i className="fas fa-chart-line text-3xl text-white mb-2 animate-pulse"></i>
                                                <p className="text-white text-sm font-semibold">View Details</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div> */}

                {/* Quick Actions */}
                {/* <div className="glass-card rounded-2xl p-8 mb-8 border border-white/10 relative overflow-hidden">
                    <div className="absolute bottom-0 right-0 w-36 h-36 bg-gradient-to-tl from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
                    
                    <div className="flex items-center mb-8 relative z-10">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-cyan-500/30 rounded-xl blur-md group-hover:blur-lg transition-all animate-pulse"></div>
                            <div className="relative w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-cyan-400/30">
                                <i className="fas fa-rocket text-xl text-cyan-400 animate-float"></i>
                            </div>
                        </div>
                        <h4 className="text-2xl font-bold text-white ml-4 animate-slide-in">Quick Actions</h4>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {quickActions.map((action, index) => (
                            <button
                                key={index}
                                className="relative group text-left"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className={`absolute inset-0 bg-gradient-to-br from-${action.bg}/20 to-${action.bg}/10 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-500`}></div>
                                <div className={`relative glass-card rounded-xl p-6 border border-white/10 group-hover:border-${action.color}-400/30 transition-all duration-300 transform group-hover:scale-105 group-hover:-translate-y-1 animate-slide-up`}>
                                    <div className="flex items-start justify-between mb-4">
                                        <div className={`w-12 h-12 bg-${action.color}-500/20 rounded-xl flex items-center justify-center group-hover:bg-${action.color}-500/30 transition-all duration-300 group-hover:scale-110`}>
                                            <i className={`${action.icon} text-xl text-${action.color}-400 group-hover:animate-bounce`}></i>
                                        </div>
                                        <i className="fas fa-external-link-alt text-gray-400 group-hover:text-white transition-colors"></i>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-white mb-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300 group-hover:bg-clip-text transition-all">
                                            {action.label}
                                        </p>
                                        <p className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                                            {action.description}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div> */}

                {/* Quick Links */}
                <div className="glass-card rounded-2xl p-8 mb-8 border border-white/10 relative overflow-hidden">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-32 bg-gradient-to-b from-pink-500/10 to-purple-500/10 rounded-full blur-3xl"></div>
                    
                    <div className="flex items-center mb-8 relative z-10">
                        <div className="relative group">
                            <div className="absolute inset-0 bg-pink-500/30 rounded-xl blur-md group-hover:blur-lg transition-all animate-pulse"></div>
                            <div className="relative w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-pink-400/30">
                                <i className="fas fa-link text-xl text-pink-400 animate-pulse"></i>
                            </div>
                        </div>
                        <h4 className="text-2xl font-bold text-white ml-4 animate-slide-in">Quick Links</h4>
                    </div>
                    
                    <div className="space-y-4">
                        <a 
                            href="https://devcore.jiricech2059.com/docs" 
                            className="group relative flex items-center p-6 glass-card rounded-xl border border-white/10 hover:border-blue-400/30 transition-all duration-500 transform hover:scale-[1.02] hover:-translate-y-1 animate-slide-up"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            <div className="relative w-14 h-14 bg-blue-500/20 rounded-xl flex items-center justify-center mr-6 group-hover:bg-blue-500/30 transition-all duration-300 group-hover:scale-110">
                                <i className="fas fa-book text-2xl text-blue-400 group-hover:animate-pulse"></i>
                            </div>
                            <div className="flex-1 relative z-10">
                                <p className="text-xl font-bold text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 group-hover:bg-clip-text transition-all mb-1">
                                    Documentation Portal
                                </p>
                                <p className="text-gray-400 group-hover:text-gray-300 transition-colors">Access comprehensive guides and API documentation</p>
                            </div>
                            <div className="relative z-10 ml-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-2">
                                <i className="fas fa-arrow-right text-blue-400 text-xl animate-pulse"></i>
                            </div>
                        </a>
                        
                        <a 
                            href="https://devcore.jiricech2059.com" 
                            className="group relative flex items-center p-6 glass-card rounded-xl border border-white/10 hover:border-purple-400/30 transition-all duration-500 transform hover:scale-[1.02] hover:-translate-y-1 animate-slide-up"
                            style={{ animationDelay: '100ms' }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            <div className="relative w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center mr-6 group-hover:bg-purple-500/30 transition-all duration-300 group-hover:scale-110">
                                <i className="fas fa-globe text-2xl text-purple-400 group-hover:animate-pulse"></i>
                            </div>
                            <div className="flex-1 relative z-10">
                                <p className="text-xl font-bold text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-400 group-hover:to-pink-400 group-hover:bg-clip-text transition-all mb-1">
                                    Main Website
                                </p>
                                <p className="text-gray-400 group-hover:text-gray-300 transition-colors">Visit the official DevCore website and services</p>
                            </div>
                            <div className="relative z-10 ml-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-2">
                                <i className="fas fa-arrow-right text-purple-400 text-xl animate-pulse"></i>
                            </div>
                        </a>
                    </div>
                </div>

                {/* Footer with Status */}
                {/* 
                <div className="flex items-center justify-between pt-8 border-t border-white/10">
                    <div className="flex items-center space-x-6">
                        <div className="glass-card rounded-xl px-4 py-3 border border-white/10 animate-slide-in">
                            <p className="text-gray-400 text-sm flex items-center">
                                <i className="fas fa-clock mr-2 animate-pulse"></i>
                                Last Updated: {lastUpdated}
                            </p>
                        </div>
                        <div className="glass-card rounded-xl px-4 py-3 border border-green-400/30 bg-green-500/10 animate-slide-in" style={{ animationDelay: '100ms' }}>
                            <p className="text-green-400 text-sm flex items-center">
                                <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                                System Status: Operational
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                        <button className="glass-card rounded-xl px-6 py-3 border border-blue-400/30 text-blue-400 hover:bg-blue-500/10 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 animate-slide-in">
                            <i className="fas fa-download mr-2"></i>
                            Export Report
                        </button>
                        <button className="glass-card rounded-xl px-6 py-3 border border-purple-400/30 text-purple-400 hover:bg-purple-500/10 transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 animate-slide-in" style={{ animationDelay: '50ms' }}>
                            <i className="fas fa-cog mr-2"></i>
                            Settings
                        </button>
                    </div>
                </div>
                */}
            </div>
        </div>
    );
};

export default AdminInfo;